import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import AdminStepUpDialog from "@/components/admin/AdminStepUpDialog";
import BrokerSurveyGate from "@/components/BrokerSurveyGate";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import EmailPreferences from "@/pages/EmailPreferences";
import AnalyzeRouteWrapper from "@/components/AnalyzeRouteWrapper";
import AnalyzeLanding from "@/pages/AnalyzeLanding";
import Watchlist from "@/pages/Watchlist";
import Pricing from "@/pages/Pricing";
import Portfolio from "@/pages/Portfolio";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import ContinueCheckout from "@/pages/ContinueCheckout";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import Terms from "@/pages/legal/Terms";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import Impressum from "@/pages/legal/Impressum";
import LegalPage from "@/pages/LegalPage";
import LegalRedirect from "@/pages/LegalRedirect";
import WithdrawalForm from "@/pages/WithdrawalForm";
import ResearchTransparency from "@/pages/ResearchTransparency";
import NotFound from "@/pages/NotFound";
import OwnerConsole from "@/pages/OwnerConsole";
import SettingsPrivacy from "@/pages/SettingsPrivacy";
import AdminDSAR from "@/pages/AdminDSAR";
import AdminSessions from "@/pages/AdminSessions";
import EarlyAccess from "@/pages/EarlyAccess";
import PrelaunchGate from "@/components/PrelaunchGate";
import GlobalErrorBoundary from "@/components/GlobalErrorBoundary";
import RouteChangeObserver from "@/components/RouteChangeObserver";
import ConsentProvider from "@/components/consent/ConsentProvider";
import { registerAnalyticsWithConsent } from "@/lib/analytics";

// WS-4 (2026-08-03): register the PostHog EU analytics adapter with
// the Consent Foundation.  This is a pure registration — it does NOT
// initialize PostHog.  The adapter's onActivate hook fires the lazy
// dynamic import only after the user grants Analytics consent.
registerAnalyticsWithConsent();

function AppRouter() {
  const location = useLocation();
  // Detect Emergent Google OAuth fragment synchronously during render to avoid race conditions
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      {/* Phase 6.AM — Emit a `route_changed` lifecycle event on every
          react-router navigation. Renders nothing. Sits here so it
          captures EVERY navigation in the SPA, including the ones
          where the destination page never gets to mount its own
          telemetry (auth gates, error boundaries, etc.). */}
      <RouteChangeObserver />
      {/* Early Access / Waitlist v1 — router-level pre-launch gate.
          Wrapping the entire ``<Routes>`` here means EVERY protected
          route is gated when ``PUBLIC_PRELAUNCH_MODE`` is on without
          duplicating per-route wrappers.  The gate uses its own
          ``isGated`` + ``isExempt`` logic on ``location.pathname``
          so (a) exempt routes (``/early-access``, legal, auth,
          preferences, owner, …) always render, (b) unknown paths
          fall through to the normal ``NotFound`` route (the gate
          only redirects explicitly gated paths), (c) when the flag
          is off, the gate is a transparent pass-through. */}
      <PrelaunchGate>
      <Routes>
      {/* Early Access / Waitlist v1 — public bilingual signup page.
          ``/waitlist`` is a client-side redirect to ``/early-access``.
          These routes are reachable regardless of PUBLIC_PRELAUNCH_MODE. */}
      <Route path="/early-access" element={<EarlyAccess />} />
      <Route path="/waitlist" element={<Navigate to="/early-access" replace />} />

      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/email-preferences" element={<EmailPreferences />} />
      <Route path="/analyze" element={<AnalyzeLanding />} />
      <Route path="/analyze/:ticker" element={<AnalyzeRouteWrapper />} />
      <Route path="/watchlist" element={<Watchlist />} />
      <Route path="/portfolio" element={<Portfolio />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/billing/continue-checkout" element={<ContinueCheckout />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      {/* Slice C — Central Research Transparency page (WS-7 / WS-1).
          Discoverable via the footer in both English and German. This
          page is a supplement to — not a replacement for — the
          canonical Financial Disclaimer, Terms, Privacy Policy and
          related legal pages. */}
      <Route path="/research-transparency" element={<ResearchTransparency />} />
      <Route path="/de/research-transparenz" element={<ResearchTransparency language="de" />} />
      <Route path="/en/research-transparency" element={<ResearchTransparency language="en" />} />
      {/* WS-11 Final Launch Reconciliation (2026-08-01) — legacy
          `/legal/*` routes are language-aware redirects to the
          canonical `/de/*` or `/en/*` route. There is exactly ONE
          canonical content source per document; legacy URLs remain
          reachable so old links continue to work but are not
          indexable as duplicates (LegalRedirect emits a noindex
          meta tag before navigating). */}
      <Route path="/legal/privacy"   element={<LegalRedirect canonicalDe="/de/datenschutz" canonicalEn="/en/privacy" legacy="/legal/privacy" fallback={<PrivacyPolicy />} />} />
      <Route path="/legal/terms"     element={<LegalRedirect canonicalDe="/de/agb"          canonicalEn="/en/terms" legacy="/legal/terms" fallback={<Terms />} />} />
      <Route path="/legal/cookies"   element={<LegalRedirect canonicalDe="/de/cookies"      canonicalEn="/en/cookies" legacy="/legal/cookies" fallback={<CookiePolicy />} />} />
      <Route path="/legal/impressum" element={<LegalRedirect canonicalDe="/de/impressum"    canonicalEn="/en/legal-notice" legacy="/legal/impressum" fallback={<Impressum />} />} />
      {/* WS-11 - deterministic factual legal pages (14 total). */}
      <Route path="/de/impressum"          element={<LegalPage documentId="de_impressum"           language="de" title="Impressum" />} />
      <Route path="/de/datenschutz"        element={<LegalPage documentId="de_datenschutz"         language="de" title="Datenschutzerklärung" />} />
      <Route path="/de/agb"                element={<LegalPage documentId="de_agb"                 language="de" title="Allgemeine Geschäftsbedingungen" />} />
      <Route path="/de/disclaimer"         element={<LegalPage documentId="de_disclaimer"          language="de" title="Finanz-Disclaimer" />} />
      <Route path="/de/cookies"            element={<LegalPage documentId="de_cookies"             language="de" title="Cookie-Richtlinie" />} />
      <Route path="/de/widerruf"           element={<LegalPage documentId="de_widerruf"            language="de" title="Widerrufsbelehrung" />} />
      <Route path="/de/widerrufsformular"  element={<WithdrawalForm language="de" />} />
      <Route path="/en/legal-notice"       element={<LegalPage documentId="en_legal_notice"        language="en" title="Legal Notice" />} />
      <Route path="/en/privacy"            element={<LegalPage documentId="en_privacy"             language="en" title="Privacy Policy" />} />
      <Route path="/en/terms"              element={<LegalPage documentId="en_terms"               language="en" title="Terms" />} />
      <Route path="/en/disclaimer"         element={<LegalPage documentId="en_disclaimer"          language="en" title="Financial Disclaimer" />} />
      <Route path="/en/cookies"            element={<LegalPage documentId="en_cookies"             language="en" title="Cookie Policy" />} />
      <Route path="/en/withdrawal"         element={<LegalPage documentId="en_withdrawal"          language="en" title="Withdrawal" />} />
      <Route path="/en/withdrawal-form"    element={<WithdrawalForm language="en" />} />
      {/* Hidden owner-only console. Not linked anywhere; gated server-side
          (email whitelist + PIN + TOTP). Non-admins see a generic 404. */}
      <Route path="/owner-console" element={<OwnerConsole />} />
      <Route path="/settings/privacy" element={<SettingsPrivacy />} />
      <Route path="/admin/dsar" element={<AdminDSAR />} />
      <Route path="/admin/sessions" element={<AdminSessions />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </PrelaunchGate>
    </>
  );
}

function App() {
  return (
    <div className="App">
      {/* Phase 6.AH — global error boundary catches ANY React render
          crash anywhere in the tree (including outside the Analyze
          subtree). On crash it: (a) shows a clean recovery UI with
          retry/reload buttons, (b) POSTs the crash details to the
          backend so admins see it in Owner Console -> Runtime Status
          even when the production frontend is otherwise unreachable. */}
      <GlobalErrorBoundary boundaryName="app_root">
        <BrowserRouter>
          <AuthProvider>
            {/* Phase 7.UI.1 — ThemeProvider sits INSIDE AuthProvider so
                it can read user.theme_preference to reconcile across
                devices. It does not gate rendering. */}
            <ThemeProvider>
              <ConsentProvider>
                <AppRouter />
                <BrokerSurveyGate />
                <ThemedToaster />
                {/* Owner-Console defect fix (Feb 2026) — global WS-9
                    step-up dialog.  Subscribes to both
                    ``adminStepUp.subscribe()`` (fetch-side) and the
                    ``stockaily:admin_stepup_required`` CustomEvent
                    (axios-side), and offers a Shadcn TOTP prompt
                    that calls ``POST /api/admin/reauth/totp`` to
                    refresh the recent-auth window.  Mounted here so
                    it is always available across the app. */}
                <AdminStepUpDialog />
              </ConsentProvider>
            </ThemeProvider>
          </AuthProvider>
        </BrowserRouter>
      </GlobalErrorBoundary>
    </div>
  );
}

/**
 * Phase 7.UI.1 — Toasts use the Sonner primitive which needs its own
 * `theme` prop (it can't read CSS variables). Wrap it so toasts always
 * match the active theme.
 */
function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme === "light" ? "light" : "dark"} richColors position="top-right" />;
}

export default App;
